import { EventEmitter, Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import {
  CropNutritionAdditionalInfoConfigItem,
  CropNutritionAdditionalInfoOption,
  CropNutritionBalance,
  CropNutritionCommodity,
  CropNutritionElement,
  CropNutritionEvaluationInstance,
  CropNutritionImproveRequest,
  CropNutritionNdviItem,
  CropNutritionStageResponse,
  ImproveCalculationEvent,
  NutritionDashboardPage,
  NutritionProviders,
} from '../../_models/nutrition';
import { environment } from '../../../../environments/environment';
import { FarmService } from '../farm.service';
import { CommonService } from '../common.service';

@Injectable()
export class NutritionService implements OnDestroy {
  private apiBaseUrl = environment.nutritionServiceUrl;
  $nutritionItems: BehaviorSubject<CropNutritionCommodity[]> = new BehaviorSubject<
    CropNutritionCommodity[]
  >(null);
  $nutritionInstance: BehaviorSubject<CropNutritionEvaluationInstance> =
    new BehaviorSubject<CropNutritionEvaluationInstance>(null);
  $nutritionSuggestion: BehaviorSubject<CropNutritionStageResponse> =
    new BehaviorSubject<CropNutritionStageResponse>(null);
  $onReloadNutrition: EventEmitter<any> = new EventEmitter();
  $onImproveCalculation: EventEmitter<ImproveCalculationEvent> = new EventEmitter();
  $onShowNutritionPaywallModal: EventEmitter<any> = new EventEmitter();
  $npkElements: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  $dashboardExpandedItems: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
  $dashboardPages: BehaviorSubject<NutritionDashboardPage[]> = new BehaviorSubject<
    NutritionDashboardPage[]
  >([]);
  $selectedCumulatedChartItemId: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  $selectedCumulatedChartElementId: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  currentPreferredProductsCountry: string;

  constructor(
    private http: HttpClient,
    private farmService: FarmService,
    private commonService: CommonService
  ) {
    this.setInitPAC();
  }

  setInitPAC() {
    this.farmService.getPreferredProductsCountry().subscribe((pac) => {
      this.currentPreferredProductsCountry = pac;
    });
  }

  /**
   * Gets the nutrition evaluations for a specific provider and year.
   *
   * @param {string} provider - The provider for which the nutrition evaluations are to be fetched.
   * @param {number} year - The year for which the nutrition evaluations are to be fetched.
   * @param {boolean} [isPolling=false] - Optional flag for polling context.
   * @returns {Observable<CropNutritionCommodity[]>} - Observable of the evaluations.
   */
  getNutritionEvaluations(
    provider: string,
    year: number,
    isPolling: boolean = false
  ): Observable<CropNutritionCommodity[]> {
    const options = {
      params: { nEE: provider, cropYear: year },
    };

    return this.http
      .get<
        CropNutritionCommodity[]
      >(`${this.apiBaseUrl}api/private/v1/cropNutritionEvaluations`, options)
      .pipe(
        map((response) => {
          const currentItems = this.$nutritionItems.getValue();
          const shouldUpdate = isPolling
            ? JSON.stringify(currentItems) !== JSON.stringify(response)
            : true;

          if (shouldUpdate) {
            this.$nutritionItems.next(response);
          }

          return this.$nutritionItems.getValue();
        }),
        catchError((error) => {
          this.$nutritionItems.next(null);
          return throwError(() => error);
        })
      );
  }

  /**
   * This method is used to get the nutrition evaluation for a specific evaluation ID.
   *
   * @param {string} evaluationId - The ID of the nutrition evaluation to be fetched.
   * @returns {Observable<CropNutritionCommodity>} - Returns an Observable that emits the nutrition evaluation as a CropNutritionCommodity object.
   */
  getNutritionEvaluation(evaluationId: string): Observable<CropNutritionCommodity> {
    return this.http
      .get<CropNutritionCommodity>(
        `${this.apiBaseUrl}api/private/v1/cropNutritionEvaluations/${evaluationId}`
      )
      .pipe(
        map((response) => response),
        catchError((error) => throwError(error))
      );
  }

  /**
   * This method is used to get the nutrition instance for a specific ID and year.
   *
   * @param {string} id - The ID of the nutrition instance to be fetched.
   * @param {number} year - The year for which the nutrition instance is to be fetched.
   * @returns {Observable<CropNutritionEvaluationInstance>} - Returns an Observable that emits the nutrition instance as a CropNutritionEvaluationInstance object.
   * The response from the server is then mapped to JSON format and the nutrition instance is updated with the response data.
   */
  getNutritionInstance(id: string, year: number): Observable<CropNutritionEvaluationInstance> {
    return this.http
      .get<CropNutritionEvaluationInstance>(
        `${this.apiBaseUrl}api/private/v1/cropNutritionEvaluationInstances/${id}`,
        {
          params: { cropYear: year },
        }
      )
      .pipe(
        map((response) => {
          this.$nutritionInstance.next(response);
          return response;
        }),
        catchError((error) => throwError(error))
      );
  }

  /**
   * This method is used to improve the nutrition evaluation for a specific evaluation ID.
   *
   * @param {string} evaluationId - The ID of the nutrition evaluation to be improved.
   * @param {CropNutritionImproveRequest} request - The request object containing the data for the improvement.
   * @param {string} provider - The provider for the nutrition evaluation.
   * @returns {Observable<any>} - Returns an Observable that emits the response from the server.
   */
  improve(
    evaluationId: string,
    request: CropNutritionImproveRequest,
    provider: string
  ): Observable<any> {
    return this.http
      .post(
        `${this.apiBaseUrl}api/private/v1/cropNutritionEvaluations/${evaluationId}/improve?nEE=${provider}`,
        request
      )
      .pipe(catchError((error) => throwError(error)));
  }

  /**
   * This method is used to get the nutrition suggestion for a specific nutrition instance.
   *
   * @param {any} instanceId - The ID of the nutrition instance for which the suggestion is to be fetched.
   * @param {string} [provider] - An optional parameter specifying the provider for the nutrition instance. If not provided, the default provider 'XFARM' will be used.
   * @returns {Observable<CropNutritionStageResponse>} - Returns an Observable that emits the nutrition suggestion as a CropNutritionStageResponse object.
   * The response from the server is then mapped to JSON format and the nutrition suggestion is updated with the response data.
   */
  getAndStoreNutritionSuggestion(
    instanceId: any,
    provider?: string
  ): Observable<CropNutritionStageResponse> {
    return this.http
      .get<CropNutritionStageResponse>(
        `${this.apiBaseUrl}api/private/v1/cropNutritionEvaluationInstances/${instanceId}/suggestion?preferredProductsCountry=` +
          (this.currentPreferredProductsCountry ? this.currentPreferredProductsCountry : 'ITA'),
        {
          params: { provider: provider ? provider : NutritionProviders.XFARM },
        }
      )
      .pipe(
        map((response) => {
          this.$nutritionSuggestion.next(null);
          this.$nutritionSuggestion.next(response);
          return response;
        }),
        catchError((error) => throwError(error))
      );
  }

  /**
   * This method is used to get the nutrition balance for a specific field.
   *
   * @param {any} nutritionId - The ID of the nutrition element for which the balance is to be fetched.
   * @param {any} fieldId - The ID of the field for which the balance is to be fetched.
   * @param {any} cropYear - The year of the crop for which the balance is to be fetched.
   * @param {any} [endDate] - An optional parameter specifying the end date for the data. If not provided, data up to the current date will be fetched.
   * @param {any} [startDate] - An optional parameter specifying the start date for the data. If not provided, data from the beginning of the year will be fetched.
   * @returns {Observable<CropNutritionBalance>} - Returns an Observable that emits the nutrition balance as a CropNutritionBalance object.
   */
  getNutritionBalance(
    nutritionId: any,
    fieldId: any,
    cropYear: any,
    endDate?: any,
    startDate?: any
  ): Observable<CropNutritionBalance> {
    const params: any = {
      elementId: nutritionId,
      cropYear,
      ...(endDate && { endDate }),
      ...(startDate && { startDate }),
    };
    return this.http
      .get<CropNutritionBalance>(
        `${this.apiBaseUrl}api/private/v1/cropNutritionBalance/fields/${fieldId}`,
        { params }
      )
      .pipe(
        map((response) => response),
        catchError((error) => throwError(error))
      );
  }
  /*currently not used, left ofr possible later use*/

  /**
   * This method is used to get the NDVI (Normalized Difference Vegetation Index) images for a specific nutrition instance.
   *
   * @param {string} nutritionInstanceId - The ID of the nutrition instance for which the NDVI data is to be fetched.
   * @param {string} [startDate] - An optional parameter specifying the start date for the data. If not provided, data from the beginning will be fetched.
   * @returns {Observable<CropNutritionNdviItem[]>} - Returns an Observable that emits the NDVI data as an array of CropNutritionNdviItem objects.
   */
  getNdviImages(
    nutritionInstanceId: string,
    startDate?: string
  ): Observable<CropNutritionNdviItem[]> {
    return this.http
      .get<CropNutritionNdviItem[]>(
        `${this.apiBaseUrl}api/private/v1/cropNutritionEvaluationInstances/${nutritionInstanceId}/ndvi`,
        {
          params: startDate ? { startDate } : {},
        }
      )
      .pipe(
        map((response) => response),
        catchError((error) => throwError(error))
      );
  }

  /**
   * This method is used to get the Biomass satellite images for a specific nutrition instance.
   * @param nutritionInstanceId
   * @param startDate
   */
  getBiomassImages(
    nutritionInstanceId: string,
    startDate?: string
  ): Observable<CropNutritionNdviItem[]> {
    return this.http
      .get<CropNutritionNdviItem[]>(
        `${this.apiBaseUrl}api/private/v1/cropNutritionEvaluationInstances/${nutritionInstanceId}/n-sensor-biomass`,
        {
          params: startDate ? { startDate } : {},
        }
      )
      .pipe(
        map((response) => response),
        catchError((error) => throwError(error))
      );
  }

  /**
   * This method is used to initialize the nutrition service with required data.
   * @returns {Observable<any>}
   */
  init(): Observable<any> {
    return this.http
      .get(`${this.apiBaseUrl}api/private/v1/engine/init`, {
        params: { nEE: 'YARA_CNP' },
      })
      .pipe(
        map((response) => response),
        catchError((error) => throwError(error))
      );
  }

  /**
   * This method is used to get the soil analysis chart data for a specific nutrition instance.
   * @param {any} instanceId - The ID of the nutrition instance for which the soil analysis chart data is to be fetched.
   * @returns {Observable<CropNutritionElement[]>}
   */
  getSoilAnalysisChartData(instanceId: any): Observable<CropNutritionElement[]> {
    return this.http
      .get<
        CropNutritionElement[]
      >(`${this.apiBaseUrl}api/private/v1/cropNutritionEvaluationInstances/${instanceId}/demand`)
      .pipe(
        map((response) => response),
        catchError((error) => throwError(error))
      );
  }

  /**
   * This method is used to get the basal dressing for a specific nutrition instance.
   * @param instanceId
   * @returns {Observable<CropNutritionElement[]>}
   */
  getBasalDressing(instanceId: any): Observable<CropNutritionElement[]> {
    return this.http
      .get<
        CropNutritionElement[]
      >(`${this.apiBaseUrl}api/private/v1/cropNutritionEvaluationInstances/${instanceId}/basalDressing`)
      .pipe(
        map((response) => response),
        catchError((error) => throwError(error))
      );
  }

  /**
   * This method is used to get the additional information for a specific nutrition evaluation.
   *
   * @param {any} evaluationInstanceId - The ID of the nutrition evaluation instance for which the additional information is to be fetched.
   * @returns {Observable<CropNutritionAdditionalInfoConfigItem>} - Returns an Observable that emits the additional information as a CropNutritionAdditionalInfo object.
   * The response from the server is then mapped to JSON format. In case of any error during the mapping, it returns null.
   */
  getAdditionalInfo(
    evaluationInstanceId: any
  ): Observable<CropNutritionAdditionalInfoConfigItem[]> {
    return this.http
      .get<
        CropNutritionAdditionalInfoConfigItem[]
      >(this.apiBaseUrl + `api/private/v1/cropNutritionEvaluationInstances/${evaluationInstanceId}/additionalParameters`)
      .pipe(
        map((response) => {
          try {
            return response;
          } catch (e) {
            return null;
          }
        })
      );
  }

  /**
   * This method is used to update the additional information for a specific nutrition evaluation, this info is then used to improve the calculation.
   *
   * @param {CropNutritionAdditionalInfoOption} data - The data object containing the additional information to be updated.
   * @param {any} evaluationInstanceId - The ID of the nutrition evaluation instance for which the additional information is to be updated.
   * @returns {Observable<any>} - Returns an Observable that emits the response from the server.
   * The response from the server is then mapped to JSON format. In case of any error during the mapping, it returns null.
   */
  updateAdditionalInfo(
    data: CropNutritionAdditionalInfoOption[],
    evaluationInstanceId: any
  ): Observable<any> {
    return this.http
      .post<any>(
        this.apiBaseUrl +
          `api/private/v1/cropNutritionEvaluationInstances/${evaluationInstanceId}/additionalParameters`,
        data
      )
      .pipe(
        map((response) => {
          try {
            return response;
          } catch (e) {
            return null;
          }
        })
      );
  }

  /**
   * This asynchronous method is used to get and store the NPK elements from the common service.
   *
   * It fetches the NPK elements by calling the `getCommonElementsByGroupName` method from the `commonService` with 'NPK' as the argument.
   * The fetched elements are then stored in the `$npkElements` BehaviorSubject.
   *
   * @returns {Promise<any>} - Returns a Promise that resolves with the fetched elements.
   * In case of any error during the fetching, it throws an Error with the error message.
   */
  async getAndStoreNpkElements() {
    try {
      const elements = await this.commonService.getCommonElementsByGroupName('NPK').toPromise();
      this.$npkElements.next(elements);
      return elements;
    } catch (e) {
      throw new Error(e);
    }
  }

  // Resets UI preferences
  onUiReset() {
    this.$dashboardExpandedItems.next([]);
    this.$dashboardPages.next([]);
  }

  ngOnDestroy() {
    this.onUiReset();
  }
}
